import { Node, mergeAttributes } from "@tiptap/core";

export interface ColumnsOptions {
  /** Default gap between columns (any CSS length). */
  gap: string;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    columns: {
      /**
       * Lay the current block selection out in `count` (2–3) newspaper-style
       * columns. If the selection is already inside a columns block, its column
       * count is changed instead of nesting a new one.
       */
      setColumns: (count: number) => ReturnType;
      /** Remove the surrounding columns block, keeping its content in the flow. */
      unsetColumns: () => ReturnType;
    };
  }
}

/**
 * Multi-column (newspaper-style) layout — Docs/Word parity.
 *
 * A `columns` block wraps a run of block content and renders it as N balanced
 * CSS columns. It is a normal editable container (like blockquote), so the
 * cursor, selection and typing all behave naturally inside it; the column flow
 * is pure CSS (see `.tiptap-columns` in TipTapEditor.css).
 *
 * The `cols` attribute (2 or 3) is persisted in the document JSON via the
 * `data-cols` HTML attribute, so on-screen layout round-trips through save/load.
 * On DOCX export the block is emitted as a borderless N-column table so the
 * side-by-side layout survives in Word (see docxExport).
 *
 * Pagination note: a columns block is treated as one unsplittable unit by the
 * paginator (like a large table/image). Keep column sections shorter than a page.
 */
export const ColumnsExtension = Node.create<ColumnsOptions>({
  name: "columns",
  group: "block",
  content: "block+",
  defining: true,
  isolating: false,

  addOptions() {
    return {
      gap: "2.5rem",
    };
  },

  addAttributes() {
    return {
      cols: {
        default: 2,
        parseHTML: el => {
          const raw = parseInt(
            (el as HTMLElement).getAttribute("data-cols") || "2",
            10
          );
          return Number.isFinite(raw) ? Math.max(2, Math.min(3, raw)) : 2;
        },
        renderHTML: attrs => ({ "data-cols": String(attrs.cols ?? 2) }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="columns"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(HTMLAttributes, {
        "data-type": "columns",
        class: "tiptap-columns",
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setColumns:
        (count: number) =>
        ({ state, commands }) => {
          const cols = Math.max(2, Math.min(3, Math.round(count) || 2));
          // Already inside a columns block? Just change the count.
          const { $from } = state.selection;
          for (let d = $from.depth; d > 0; d--) {
            if ($from.node(d).type.name === this.name) {
              return commands.updateAttributes(this.name, { cols });
            }
          }
          return commands.wrapIn(this.name, { cols });
        },

      unsetColumns:
        () =>
        ({ state, tr, dispatch }) => {
          const { $from } = state.selection;
          for (let d = $from.depth; d > 0; d--) {
            const node = $from.node(d);
            if (node.type.name === this.name) {
              if (dispatch) {
                const start = $from.before(d);
                const end = $from.after(d);
                // Replace the wrapper with its own content, unwrapping it.
                tr.replaceWith(start, end, node.content);
              }
              return true;
            }
          }
          return false;
        },
    };
  },
});

export default ColumnsExtension;
